import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import { VisitSummaryTemplateFormFragment } from "./fragments.generated";

type Props = {
    initialData: VisitSummaryTemplateFormFragment;
    onSubmit: (template: unknown) => void;
    error?: ApolloError;
};

function printTemplate(template: unknown): string {
    return template === null || template === undefined ? "" : JSON.stringify(template, null, 2);
}

export default function VisitSummaryTemplateForm({ initialData, onSubmit, error }: Props) {
    const { t } = useTranslation("researchType");
    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<VisitSummaryTemplateFormInputs>({
        defaultValues: { template: printTemplate(initialData.visitSummaryTemplate) },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    /**
     * Only the syntax is checked here; the server checks the template against the report engine.
     * An empty field is not a mistake - it is how a template is removed - so it goes as null.
     */
    const handleValid = ({ template }: VisitSummaryTemplateFormInputs) => {
        if (template.trim().length === 0) {
            onSubmit(null);
            return;
        }

        try {
            onSubmit(JSON.parse(template));
        } catch {
            setError("template", { message: t("visitSummaryTemplateForm.errorInvalidJson") });
        }
    };

    return (
        <form onSubmit={handleSubmit(handleValid)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("template")}
                    label={t("visitSummaryTemplateForm.labelTemplate")}
                    error={!!errors.template}
                    helperText={errors.template?.message ?? t("visitSummaryTemplateForm.helperText")}
                    slotProps={{ htmlInput: { style: { fontFamily: "monospace" }, spellCheck: false } }}
                    minRows={20}
                    multiline
                />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("visitSummaryTemplateForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type VisitSummaryTemplateFormInputs = {
    template: string;
};
